/**
 * Keypoint schema configuration loader.
 *
 * This module loads the keypoint, skeleton, and color configuration from a YAML file.
 */

import configData from './keypoint-schema.yml'

export interface KeypointDefinition {
  name: string
  display_order: number
}

export interface KeypointSchemaConfig {
  keypoints: KeypointDefinition[]
  skeleton: number[][]
  colors: string[]
}

// Type the imported config data
const config = configData as KeypointSchemaConfig

/**
 * Get the full keypoint configuration.
 */
export function getKeypointConfig(): KeypointSchemaConfig {
  return config
}

/**
 * Get ordered list of keypoint names.
 */
export function getKeypointNames(): string[] {
  return config.keypoints.map(kp => kp.name)
}

/**
 * Get skeleton connections.
 * @returns List of [start, end] pairs representing bone connections (1-based indices).
 */
export function getSkeletonSchema(): number[][] {
  return config.skeleton
}

/**
 * Get color palette.
 */
export function getColors(): string[] {
  return config.colors
}

/**
 * Get color for a specific keypoint index.
 * Colors cycle if there are more keypoints than colors.
 */
export function getKeypointColor(index: number): string {
  const colors = config.colors
  return colors[index % colors.length]
}

/**
 * Get the display order for keypoints.
 *
 * @returns List of keypoint indices sorted by their display_order.
 * For example, if earR (index 20) has display_order=1 and earL (index 21)
 * has display_order=2, the returned list starts with [20, 21, ...].
 */
export function getDisplayOrder(): number[] {
  const keypointsWithIndex = config.keypoints.map((kp, idx) => ({
    idx,
    order: kp.display_order,
  }))
  const sorted = keypointsWithIndex.sort((a, b) => a.order - b.order)
  return sorted.map(item => item.idx)
}

/**
 * Get the number of keypoints defined in the schema.
 */
export function getKeypointCount(): number {
  return config.keypoints.length
}
